'use client';

import Image from 'next/image';
import Link from 'next/link';
import { PrismicNextImage } from '@prismicio/next';
import type { ImageFieldImage } from '@prismicio/client';
import { CarouselNavButtons } from '@/components/redesign/PhaseCarouselCard';
import { EbookAuthorBio } from '../../EbookAuthorBio';

const DEVSU_LOGO_URL =
  'https://images.prismic.io/devsucom2024/ZyO63a8jQArT0F9p_DevsuLogo.png?auto=format%2Ccompress&fit=max&w=1080';

interface EbookCarouselCardProps {
  href: string;
  title: string;
  image: ImageFieldImage | null;
  author: {
    name: string;
    href: string;
    avatar: ImageFieldImage | null;
    bio: string | null;
  };
  atStart: boolean;
  atEnd: boolean;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * Ebook card for the ExpertsInPlaybooks carousel. Structurally mirrors
 * ProcessOverviewThreeStep's PhaseCarouselCard (content column on the left,
 * media column on the right) but swaps the phase icon/description/CTA for the
 * ebook-specific EBOOK kicker, title, author bio card and "Read ebook" button.
 */
export function EbookCarouselCard({
  href,
  title,
  image,
  author,
  atStart,
  atEnd,
  onPrev,
  onNext,
}: EbookCarouselCardProps) {
  return (
    <>
      {/* Content column */}
      <div className="relative flex shrink-0 flex-col gap-6 overflow-hidden bg-[#f3f3f3] p-6 md:w-full lg:w-[340px] xl:w-[496px]">
        <CarouselNavButtons
          atStart={atStart}
          atEnd={atEnd}
          onPrev={onPrev}
          onNext={onNext}
        />

        <div className="flex flex-col gap-4">
          {/* EBOOK kicker with purple accent bar */}
          <div className="flex items-center border-l-2 border-[#4b0fea] pl-3">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-[#041118]">
              EBOOK
            </span>
          </div>

          {/* Title */}
          <h3 className="text-[22px] font-semibold leading-[1.25] text-[#041118] md:text-[24px]">
            {title}
          </h3>

          {/* Author card */}
          <Link
            href={author.href}
            className="mt-2 flex flex-col gap-2 rounded-2xl bg-white p-4 transition-colors hover:bg-white/80"
          >
            <div className="flex items-center gap-4">
              {author.avatar?.url ? (
                <PrismicNextImage
                  field={author.avatar}
                  className="size-[43px] shrink-0 rounded-lg object-cover"
                  width={43}
                  height={43}
                  loading="lazy"
                  imgixParams={{ fit: 'crop', ar: '1:1', q: 75 }}
                />
              ) : (
                <Image
                  src={DEVSU_LOGO_URL}
                  alt={author.name}
                  width={43}
                  height={43}
                  className="size-[43px] shrink-0 rounded-lg object-cover"
                  loading="lazy"
                />
              )}
              <span className="text-base font-semibold text-[#041118]">
                {author.name}
              </span>
            </div>
            {author.bio && (
              <div className="text-[#041118]/80">
                {/* EbookAuthorBio is white-on-dark by default; wrap so the
                    inherited color from this light card takes over. */}
                <div className="[&_button]:text-[#041118]/60 [&_button]:hover:text-[#041118] [&_p]:text-[#041118]/80">
                  <EbookAuthorBio bio={author.bio} />
                </div>
              </div>
            )}
          </Link>

          {/* CTA */}
          <Link
            href={href}
            className="mt-2 inline-flex w-fit items-center gap-2 rounded-lg bg-[#05f273] px-6 py-3 text-[16px] font-medium text-[#041118] transition-colors hover:bg-[#04d666]"
          >
            Read ebook
          </Link>
        </div>
      </div>

      {/* Media column (image only — no video for the ebook variant) */}
      {image?.url && (
        <div className="relative min-h-[280px] flex-1 lg:min-h-[427px]">
          <PrismicNextImage
            field={image}
            fill
            sizes="(min-width: 768px) 784px, 100vw"
            className="object-cover"
            fallbackAlt=""
          />
        </div>
      )}
    </>
  );
}
